import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { User, NotebookPen, FileText, BookOpen, Settings, LogOut, UserCog } from 'lucide-react';
import { toast } from 'sonner';

const Profile = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);

      if (user) {
        const { data, error } = await supabase
          .from('users')
          .select('role')
          .eq('id', user.id)
          .single();
        
        if (data) {
          setUserRole(data.role);
        }
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getUserName = () => {
    if (!user) return 'User';
    return user.email?.split('@')[0] || 'User';
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      toast.error('Error logging out');
    }
  };

  const menuItems = [
    {
      icon: NotebookPen,
      label: 'Personal Notes',
      path: '/personal-notes',
      color: 'text-blue-400'
    },
    {
      icon: FileText,
      label: 'Documents',
      path: '/documents',
      color: 'text-gray-300'
    },
    {
      icon: BookOpen,
      label: 'Daily Standup',
      path: '/logger',
      color: 'text-gray-300'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0e1a] p-4 lg:p-8">
      <div className="max-w-2xl mx-auto">
        {/* User Profile Card */}
        <div className="bg-[#1a1f2e] rounded-2xl border border-white/10 p-6 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
              <User size={32} />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-semibold text-white mb-1">{getUserName()}</h2>
              <p className="text-sm text-gray-400 truncate">{user?.email}</p>
              {userRole && (
                <span className="inline-block mt-2 px-3 py-1 text-xs font-medium rounded-full bg-blue-500/10 text-blue-400 capitalize">
                  {userRole}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Menu Items */}
        <div className="bg-[#1a1f2e] rounded-2xl border border-white/10 overflow-hidden mb-6">
          {menuItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <button
                key={index}
                onClick={() => navigate(item.path)}
                className="w-full flex items-center gap-4 px-6 py-4 hover:bg-white/5 active:bg-white/10 transition-colors border-b border-white/5 last:border-b-0"
              >
                <Icon size={24} className={item.color} />
                <span className="text-base font-medium text-gray-200">{item.label}</span>
              </button>
            );
          })}
        </div>

        {/* Settings & Admin */}
        <div className="bg-[#1a1f2e] rounded-2xl border border-white/10 overflow-hidden mb-6">
          {userRole === 'admin' && (
            <button
              onClick={() => navigate('/admin/users')}
              className="w-full flex items-center gap-4 px-6 py-4 hover:bg-white/5 active:bg-white/10 transition-colors border-b border-white/5"
            >
              <UserCog size={24} className="text-purple-400" />
              <span className="text-base font-medium text-gray-200">User Management</span>
            </button>
          )}
          <button
            onClick={() => navigate('/settings')}
            className="w-full flex items-center gap-4 px-6 py-4 hover:bg-white/5 active:bg-white/10 transition-colors"
          >
            <Settings size={24} className="text-gray-300" />
            <span className="text-base font-medium text-gray-200">Settings</span>
          </button>
        </div>

        {/* Logout */}
        <div className="bg-[#1a1f2e] rounded-2xl border border-white/10 overflow-hidden">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-4 px-6 py-4 hover:bg-red-500/10 active:bg-red-500/20 transition-colors"
          >
            <LogOut size={24} className="text-red-400" />
            <span className="text-base font-medium text-red-400">Logout</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Profile;
